import { ticketExportFor, type TicketArtwork, type TicketGeometry } from "@/lib/ticketLayout";
import type { TicketData } from "@/types/journey";

/* ============================================================
   船票导出（PNG）
   ------------------------------------------------------------
   底图、画布尺寸与字段坐标都来自 src/lib/ticketLayout.ts —— 与屏幕上的票面
   **同一份定义**，这样"看到的"和"下载到的"才是同一张票。

   按视口档位出图：桌面端给横版票版 + 1920×1080，移动端给竖版 + 1080×1440。
   （此前写死竖版，桌面用户下载到的是手机版画面。）

   绘制顺序：素材按 cover 铺满画布 → 标题 / 我的答案 / 答案 / 编号 → 朱印里的「已启航」。
   ============================================================ */

type ExportProfile = "mobile" | "tablet" | "desktop";

const SERIF = "'Noto Serif SC', 'Songti SC', serif";
const SANS = "Inter, 'Helvetica Neue', sans-serif";

const INK = "#342d25";
const CINNABAR = "#8e2f22";
const LABEL_COLOR = "#a8332b";
const MUTED = "rgba(94, 52, 28, .72)";

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const character of Array.from(text)) {
    const next = line + character;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = character;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [""];
}

/**
 * 把行动文字排进给定宽度。
 * 不传 options 时保持历史契约（54 起步、四行以内、兜底 28）——单测依赖它。
 */
export function fitActionText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  options: { baseSize?: number; maxLines?: number; lineHeight?: number; maxHeight?: number } = {},
): { fontSize: number; lines: string[] } {
  const { baseSize = 54, maxLines = 4, lineHeight = 1.36, maxHeight = Number.POSITIVE_INFINITY } = options;
  const steps = baseSize === 54
    ? [54, 50, 46, 42, 38, 34, 30]
    : [baseSize, baseSize * 0.92, baseSize * 0.86, baseSize * 0.8, baseSize * 0.74];
  for (const fontSize of steps) {
    ctx.font = `700 ${fontSize}px ${SERIF}`;
    const lines = wrapText(ctx, text, maxWidth);
    if (lines.length <= maxLines && lines.length * fontSize * lineHeight <= maxHeight) {
      return { fontSize, lines };
    }
  }
  ctx.font = `700 28px ${SERIF}`;
  return { fontSize: 28, lines: wrapText(ctx, text, maxWidth) };
}

function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("TICKET_BASE_UNAVAILABLE"));
    image.src = source;
  });
}

export async function renderTicketPngBlob(ticket: TicketData, profile: ExportProfile = "mobile"): Promise<Blob> {
  const { art, geometry: G, canvas: size } = ticketExportFor(profile);
  const ART: TicketArtwork = art;
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("CANVAS_UNAVAILABLE");

  /* 与 .v2-background-image 的 object-fit: cover 同一套算法：
     取能覆盖画布的那一维，再居中裁剪（横版画布与素材同比例，等于不裁）。 */
  const image = await loadImage(ART.src);
  const scale = Math.max(canvas.width / ART.width, canvas.height / ART.height);
  const drawWidth = ART.width * scale;
  const drawHeight = ART.height * scale;
  const originX = (canvas.width - drawWidth) / 2;
  const originY = (canvas.height - drawHeight) / 2;
  ctx.drawImage(image, originX, originY, drawWidth, drawHeight);

  /* 素材归一化坐标 → 画布像素 */
  const px = (value: number) => originX + value * drawWidth;
  const py = (value: number) => originY + value * drawHeight;
  const fontOf = (ratio: number) => ratio * drawWidth;

  const boxLeft = px(G.fields.left);
  const boxWidth = G.fields.width * drawWidth;
  const blockTop = py(G.fields.top);
  const headSize = fontOf(G.font.head);
  const labelSize = fontOf(G.font.label);
  const answerBase = fontOf(G.font.answer);
  const noSize = fontOf(G.font.no);
  const gapLabel = G.gap.label * drawWidth;
  const gapAnswer = G.gap.answer * drawWidth;
  const gapNo = G.gap.no * drawWidth;

  /* 先量出行动文字要占几行、多大字号，才能算出整块高度去做旋转 */
  const layout = fitActionText(ctx, ticket.action, boxWidth, {
    baseSize: answerBase,
    maxLines: G.answerLines,
    lineHeight: G.answerLineHeight,
    maxHeight: G.answerLines * answerBase * G.answerLineHeight,
  });
  const lineStep = layout.fontSize * G.answerLineHeight;

  const labelTop = blockTop + headSize * 1.2 + gapLabel;
  const answerTop = labelTop + labelSize + gapAnswer;
  const answerBottom = answerTop + lineStep * layout.lines.length;
  const noBaseline = answerBottom + gapNo + noSize * 0.8;
  const blockHeight = noBaseline - blockTop;

  /* 文字块整体旋转（竖版为 0，横版约 7°），绕块中心旋转，与 CSS transform 一致 */
  const blockCenterX = boxLeft + boxWidth / 2;
  const blockCenterY = blockTop + blockHeight / 2;

  ctx.save();
  ctx.translate(blockCenterX, blockCenterY);
  ctx.rotate((G.fieldsRotate * Math.PI) / 180);
  ctx.translate(-blockCenterX, -blockCenterY);
  /* 与屏幕一致：对齐方式取自布局定义（移动居中、桌面左对齐），
     文字块整体旋转角度也与屏幕同一个值——正着的字压在斜着的票上会一眼看出是贴上去的。 */
  const alignCenter = G.align === "center";
  const anchorX = alignCenter ? blockCenterX : boxLeft;
  ctx.textAlign = alignCenter ? "center" : "left";
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = CINNABAR;
  ctx.font = `400 ${headSize}px ${SERIF}`;
  ctx.fillText("红船青年船票", anchorX, blockTop + headSize * 0.86);

  ctx.fillStyle = LABEL_COLOR;
  ctx.font = `400 ${labelSize}px ${SANS}`;
  ctx.fillText("我 的 答 案", anchorX, labelTop + labelSize * 0.82);

  ctx.fillStyle = INK;
  ctx.font = `700 ${layout.fontSize}px ${SERIF}`;
  layout.lines.forEach((line, index) => {
    ctx.fillText(line, anchorX, answerTop + lineStep * (index + 1) - lineStep * 0.24);
  });

  ctx.fillStyle = MUTED;
  ctx.font = `400 ${noSize}px ${SANS}`;
  ctx.fillText(`No. ${ticket.ticketCode}`, anchorX, noBaseline);

  ctx.restore();

  /* 朱印里的「已启航」：与屏幕同一个印章方框 */
  const sealCenterX = px(G.seal.left + G.seal.width / 2);
  const sealCenterY = py(G.seal.top + G.seal.height / 2);
  const sealSize = fontOf(G.font.seal);
  ctx.save();
  ctx.translate(sealCenterX, sealCenterY);
  ctx.rotate((G.sealRotate * Math.PI) / 180);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#fff4e2";
  ctx.font = `400 ${sealSize}px ${SERIF}`;
  ctx.fillText("已启航", 0, sealSize * 0.03);
  ctx.restore();

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => value ? resolve(value) : reject(new Error("PNG_EXPORT_FAILED")), "image/png");
  });
}

export async function downloadTicketPng(ticket: TicketData, filename = "red-boat-ticket.png", profile: ExportProfile = "mobile"): Promise<void> {
  const blob = await renderTicketPngBlob(ticket, profile);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
